import axios from 'axios';
import qs from 'qs';
import dayjs from 'dayjs';
import type {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosRequestTransformer,
} from 'axios';

export type ApiMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE';

export class HttpError extends Error {
  axiosError: AxiosError;
  clientMessage?: string;
  data?: Record<string, string>;

  constructor(error: AxiosError) {
    super(error.message);
    this.axiosError = error;

    this.data = (this.axiosError.response?.data || {}) as Record<string, string>;
    this.clientMessage = this.data.message || this.axiosError.message || 'An error occurred.';

    // Set the prototype explicitly.
    Object.setPrototypeOf(this, HttpError.prototype);
  }

  get statusCode(): number | undefined {
    return this.axiosError.response?.status;
  }

  get stuutErrorCode(): string | undefined {
    return this.data?.error || '';
  }
}

const dateTransformer = (data: any): any => {
  if (data instanceof FormData) {
    // don't transform FormData (e.g. file submissions)
    return data;
  }
  if (data instanceof Date) {
    return data.toISOString();
  }

  if (dayjs.isDayjs(data)) {
    return data.toISOString();
  }

  if (Array.isArray(data)) {
    return data.map(dateTransformer);
  }

  if (typeof data === 'object' && data !== null) {
    return Object.fromEntries(
      Object.entries(data as object).map(([key, value]) => [key, dateTransformer(value)]),
    );
  }

  return data;
};

/**
 * Params serializer for v2 API endpoints.
 * Uses 'repeat' format: param=a&param=b (OpenAPI/generated SDK compatible)
 */
export const v2ParamsSerializer = (params: Record<string, any>): string => {
  const updatedParams: Record<string, any> = {};
  Object.keys(params).forEach((key) => {
    updatedParams[key] = dateTransformer(params[key]) as string;
  });
  return qs.stringify(updatedParams, {
    arrayFormat: 'repeat',
    strictNullHandling: true,
  });
};

export class ApiClient {
  client: AxiosInstance;

  constructor(baseUrl: string, authToken?: string | null) {
    this.client = axios.create({
      baseURL: baseUrl,
      timeout: 60000,
      headers: {
        'Content-Type': 'application/json',
      },

      transformRequest: [
        // (data, headers) => headers['Content-Type'] == 'multipart/form-data' ? data : dateTransformer(data),
        dateTransformer,
        ...(axios.defaults.transformRequest as AxiosRequestTransformer[]),
      ],

      paramsSerializer: (params): string => {
        const updatedParams: Record<string, any> = {};
        Object.keys(params).forEach((key) => {
          updatedParams[key] = dateTransformer(params[key]) as string;
        });

        try {
          return qs.stringify(updatedParams, {
            arrayFormat: 'brackets',
            strictNullHandling: true,
          });
        } catch (e) {
          console.error(e);
          return '';
        }
      },
    });
    this.setAuthToken(authToken);

    this.client.interceptors.response.use(
      (response) => {
        return response;
      },
      (error: AxiosError) => {
        return Promise.reject(this.handleError(error));
      },
    );
  }

  get baseUrl(): string {
    return this.client.defaults.baseURL || '';
  }

  set baseUrl(url: string) {
    this.client.defaults.baseURL = url;
  }

  get authHeader(): string | null {
    const authorization = this.client.defaults.headers.common.Authorization;
    return typeof authorization === 'string' ? authorization : null;
  }

  setAuthToken(token?: string | null): void {
    this.client.defaults.headers.common.Authorization = token ? `Bearer ${token}` : null;
  }

  async delete(endpoint: string, args?: AxiosRequestConfig): Promise<AxiosResponse> {
    return this.request('DELETE', endpoint, args);
  }

  async get(endpoint: string, args?: AxiosRequestConfig): Promise<AxiosResponse> {
    return this.request('GET', endpoint, args);
  }

  async post(endpoint: string, args?: AxiosRequestConfig): Promise<AxiosResponse> {
    return this.request('POST', endpoint, args);
  }

  async patch(endpoint: string, args?: AxiosRequestConfig): Promise<AxiosResponse> {
    return this.request('PATCH', endpoint, args);
  }

  async request(
    method: ApiMethod,
    endpoint: string,
    args?: AxiosRequestConfig,
  ): Promise<AxiosResponse> {
    const axiosRequestArgs = {
      method,
      url: endpoint,
      ...(args || {}),
    };

    return this.client.request(axiosRequestArgs);
  }

  handleError(error: AxiosError): HttpError {
    const httpError = new HttpError(error);

    if (httpError.data?.error) {
      const errorCodeMap: Record<string, string> = {
        HTTPAuthenticationError: 'AuthenticationError',
        HTTPForbiddenError: 'AuthorizationError',
        HTTPNotFoundError: 'EntityNotFoundError',
        HTTPValidationError: 'ValidationError',
        HTTPRateLimitError: 'RateLimitError',
      };

      const normalizedError = errorCodeMap[httpError.data.error];
      if (normalizedError) {
        httpError.data.error = normalizedError;
      }
    }

    return httpError;
  }
}
